/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { IconName } from '@zapier/design-system';
import { Icon, Link, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { useUniversalNavContext } from '../../../../hooks/useUniversalNavContext';
import { emitUserInteractionEvent } from '../../../../utils/avroEvents';
import { ArrowLinkIcon } from '../../ArrowLinkIcon';
import type { NavItemProduct2 } from '../../../../constants/links';

type MegaMenuProductRightProps = {
  header: NavItemProduct2['promoArea']['header'];
  submenu: NavItemProduct2['promoArea']['submenu'];
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
};

const Styles = labelStyles('StaticProductRight', {
  root: css({
    display: 'flex',
    flexDirection: 'column',
    // necessary to prevent the right side of focus outlines from being hidden
    marginRight: 1,
    width: 300,
    // Undoes UniversalTopbarLink style, which makes all text bold when toggle is hovered/focused
    fontWeight: 'initial',
  }),
  header: css({
    display: 'flex',
    flexDirection: 'column',
    textDecoration: 'none',
  }),
  linkHeader: css({
    '&:hover [data-tag="heading"]': {
      color: 'var(--zds-brand-orange, #FF4F00)',
      textDecoration: 'none',
    },
    '&:hover [data-tag="subheading"]': {
      textDecoration: 'underline',
    },
    '&:hover [data-tag="arrow-icon-wrapper"]': {
      marginLeft: 4,
      opacity: 1,
    },
    '&:focus': {
      outline: `var(--zds-border-focus, 2px solid #695BE8)`,
    },
  }),
  heading: css({
    font: `var(--zds-section-header, 600 24px/36px ${Typography.pageHeader1.fontFamily})`,
    color: 'var(--zds-text-stronger, #201515)',
    lineHeight: '24px',
    fontSize: '30px',
    fontWeight: 600,
    letterSpacing: 1,
  }),
  subheading: css({
    font: `var(--zds-small-print-1, 400 13px/20px ${Typography.base.fontFamily})`,
    color: 'var(--zds-gray-warm-9, #413735)',
    display: 'block',
    marginTop: 8,
  }),
  divider: css({
    margin: '16px 0',
    height: 1,
    backgroundColor: 'var(--zds-background-strongest, #ECE9DF)',
  }),
  linkContainer: css({
    display: 'flex',
    columnGap: 6,
    marginBottom: 18,
    textDecoration: 'none',
    color: 'var(--zds-text-stronger, #201515)',
    '&:hover [data-tag="link-heading"]': {
      color: 'var(--zds-brand-orange, #FF4F00)',
      textDecoration: 'none',
    },
    '&:hover [data-tag="link-icon"]': {
      color: 'var(--zds-brand-orange, #FF4F00)',
    },
    '&:hover [data-tag="tag-label"]': {
      textDecoration: 'none',
    },
    '&:hover [data-tag="link-description"]': {
      textDecoration: 'underline',
    },
    '&:hover [data-tag="arrow-icon-wrapper"]': {
      marginLeft: 4,
      opacity: 1,
      color: 'var(--zds-gray-warm-9, #413735)',
    },
    '&:focus': {
      outline: `1px solid var(--zds-ui-primary-strongest, #503EBD)`,
    },
  }),
  linkIcon: css({
    marginTop: 5,
  }),
  linkTextContainer: css({
    display: 'flex',
    flexDirection: 'column',
  }),
  linkHeading: css({
    font: `var(--zds-body-3, 600 14px/24px ${Typography.base.fontFamily})`,
    lineHeight: '30px',
    fontWeight: 500,
  }),
  linkHeadingRow: css({
    display: 'flex',
    alignItems: 'center',
    lineHeight: '30px',
    fontWeight: 500,
  }),
  descriptionWrapper: css({
    font: `var(--zds-small-print-1, 400 13px/20px ${Typography.base.fontFamily})`,
    lineHeight: '20px',
  }),
  linkDescription: css({
    font: `var(--zds-small-print-1, 400 13px/20px ${Typography.base.fontFamily})`,
    color: 'var(--zds-gray-warm-9, #413735)',
    lineHeight: '20px',
  }),
  tagLabel: css({
    // TODO: remove hardcoded fallbacks when PDL design tokens are fully in use
    font: Typography.base.fontFamily,
    marginLeft: '6px',
    fontSize: '14px',
    lineHeight: '24px',
    fontWeight: 500,
    padding: 'var(--zds-space-0, 0) var(--zds-space-4, 4px)',
    border: '1px solid var(--zds-stroke-default, #b5b2aa)',
    borderRadius: 'var(--zds-radius-small, 3px)',
    backgroundColor: 'transparent',
    textDecoration: 'none',
  }),
});

/**
 * @private
 * Wrapper for the content inside the Product tab's right side
 */
const MegaMenuProductRight = ({
  header,
  submenu,
  onCloseMenu,
}: MegaMenuProductRightProps) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const handleEvent = (text: string, eventType: string) => {
    emitUserInteractionEvent({
      event_action: eventType,
      option_selected: `Product tab Right: ${text}`,
    });

    if (eventType === 'click' && onCloseMenu) onCloseMenu();
  };

  const Header = () => {
    if (header.href) {
      return (
        <Link
          color={null}
          css={[Styles.header, Styles.linkHeader]}
          href={getFullyQualifiedURL(header.href)}
          target={header.target}
          onClick={() => handleEvent('Early Access Program heading', 'click')}
          onMouseEnter={() =>
            handleEvent('Early Access Program heading', 'hover')
          }
        >
          <span css={Styles.heading} data-tag="heading" data-zds>
            {header.heading}
          </span>
          {header.subheading && (
            <div data-zds>
              <span css={Styles.subheading} data-tag="subheading" data-zds>
                {header.subheading}
                <ArrowLinkIcon />
              </span>
            </div>
          )}
        </Link>
      );
    } else {
      return (
        <header css={Styles.header} data-zds>
          <span css={Styles.heading} data-tag="heading" data-zds>
            {header.heading}
          </span>
          <div data-zds>
            <span css={Styles.subheading} data-tag="subheading" data-zds>
              {header.subheading}
            </span>
          </div>
        </header>
      );
    }
  };

  return (
    <div css={Styles.root} data-zds>
      <Header />

      <hr css={Styles.divider} data-zds />

      <ul data-zds>
        {submenu.map((link) => (
          <li key={link.text} data-zds>
            <Link
              color={null}
              css={Styles.linkContainer}
              href={getFullyQualifiedURL(link.href)}
              target={link.target}
              onClick={() => handleEvent(link.text, 'click')}
              onMouseEnter={() => handleEvent(link.text, 'hover')}
            >
              <span css={Styles.linkIcon} data-tag="link-icon" data-zds>
                <Icon name={link.icon as IconName} size={18} />
              </span>
              <div css={Styles.linkTextContainer} data-zds>
                <div css={Styles.linkHeadingRow} data-zds>
                  <span
                    css={Styles.linkHeading}
                    data-tag="link-heading"
                    data-zds
                  >
                    {link.text}
                  </span>
                  {link.tag && (
                    <span css={Styles.tagLabel} data-tag="tag-label" data-zds>
                      {link.tag}
                    </span>
                  )}
                </div>
                <div css={Styles.descriptionWrapper} data-zds>
                  <span
                    css={Styles.linkDescription}
                    data-tag="link-description"
                    data-zds
                  >
                    {link.description}
                  </span>
                  <ArrowLinkIcon />
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MegaMenuProductRight;
